import { useMemo } from 'react'
import { RunWithDetails } from '../../types'
import { SegmentPlanBlock } from './SegmentPlanBlock'
import { AidStationPlanBlock } from './AidStationPlanBlock'
import { NutritionSummary } from './NutritionSummary'
import { calculateSegmentTimeline } from '../../lib/timeline'
import { Separator } from '../ui/separator'
import { useCalibrationProfile, useDefaultCalibrationProfile } from '../../hooks/useCalibrationProfiles'

interface PlanTabProps {
  run: RunWithDetails
}

export function PlanTab({ run }: PlanTabProps) {
  // Get calibration profile for nutrition targets
  const defaultProfile = useDefaultCalibrationProfile()
  const { data: calibrationProfile } = useCalibrationProfile(run.calibration_profile_id || '')
  const activeProfile = calibrationProfile || defaultProfile

  // Calculate segment data using centralized timeline (includes aid station stops)
  const segmentData = useMemo(() => {
    if (!run.segments) return []

    const timeline = calculateSegmentTimeline(
      run.segments,
      run.aid_stations || [],
      activeProfile
    )

    return timeline.segments.map((segmentData, index) => ({
      segment: segmentData.segment,
      index,
      pace: segmentData.pace,
      segmentTime: segmentData.totalTime, // Includes segment stop time
      cumulativeDistance: segmentData.cumulativeDistance,
      cumulativeTime: segmentData.departureTime // Includes aid station stops!
    }))
  }, [run.segments, run.aid_stations, activeProfile])

  // Group plan items by location
  const planItemsByLocation = useMemo(() => {
    const bySegment: Record<string, any[]> = {}
    const byAidStation: Record<string, any[]> = {}
    
    run.plan_items?.forEach(item => {
      if (item.segment_id) {
        if (!bySegment[item.segment_id]) bySegment[item.segment_id] = []
        bySegment[item.segment_id].push(item)
      } else if (item.aid_station_id) {
        if (!byAidStation[item.aid_station_id]) byAidStation[item.aid_station_id] = []
        byAidStation[item.aid_station_id].push(item)
      }
    })
    
    return { bySegment, byAidStation }
  }, [run.plan_items])

  // Create a merged timeline of segments and aid stations
  const timeline = useMemo(() => {
    const items: Array<{ type: 'segment' | 'aid_station', data: any, distance: number }> = []

    // Add segments - position at their END distance (when completed)
    segmentData.forEach(({ segment, index, pace, segmentTime, cumulativeDistance, cumulativeTime }) => {
      items.push({
        type: 'segment',
        data: { segment, index, pace, segmentTime, cumulativeDistance, cumulativeTime },
        distance: cumulativeDistance // End of segment (when completed)
      })
    })

    // Add aid stations - position at their actual distance
    run.aid_stations?.forEach(aidStation => {
      items.push({
        type: 'aid_station',
        data: aidStation,
        distance: aidStation.at_distance_km
      })
    })

    // Sort everything by distance for consistent chronological timeline
    return items.sort((a, b) => a.distance - b.distance)
  }, [segmentData, run.aid_stations])

  if (!run.segments || run.segments.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-20">
          <p className="text-neutral-500">Add some segments to your course first to start planning nutrition.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-primary-900 mb-2">Nutrition & Hydration Plan</h2>
        <p className="text-sm text-neutral-600">
          Plan your nutrition and hydration strategy for each segment and aid station.
        </p>
      </div>

      <NutritionSummary runId={run.id} />

      <Separator className="bg-neutral-200" />

      <div className="space-y-6">
        {timeline.map((item, timelineIndex) => {
          if (item.type === 'segment') {
            const { segment, index, pace, segmentTime, cumulativeDistance, cumulativeTime } = item.data
            const planItems = planItemsByLocation.bySegment[segment.id] || []

            return (
              <SegmentPlanBlock
                key={`segment-${segment.id}`}
                segment={segment}
                segmentIndex={index}
                planItems={planItems}
                cumulativeDistance={cumulativeDistance}
                pace={pace}
                segmentTime={segmentTime}
                cumulativeTime={cumulativeTime}
                nutritionTargets={{
                  carbs_g_per_h: {
                    low: activeProfile.target_carbs_g_per_h_low || 60,
                    high: activeProfile.target_carbs_g_per_h_high || 90
                  },
                  fluids_ml_per_h: {
                    low: activeProfile.target_fluids_ml_per_h_low || 500,
                    high: activeProfile.target_fluids_ml_per_h_high || 750
                  },
                  sodium_mg_per_h: {
                    low: activeProfile.target_sodium_mg_per_h_low || 300,
                    high: activeProfile.target_sodium_mg_per_h_high || 800
                  }
                }}
              />
            )
          } else {
            const aidStation = item.data
            const planItems = planItemsByLocation.byAidStation[aidStation.id] || []

            return (
              <AidStationPlanBlock
                key={`aid-station-${aidStation.id}`}
                aidStation={aidStation}
                planItems={planItems}
              />
            )
          }
        })}
      </div>

      {timeline.length === 0 && (
        <div className="text-center py-12">
          <p className="text-neutral-500">
            No segments or aid stations to plan for yet.
          </p>
        </div>
      )}
    </div>
  )
}